import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { mergeModelOptionsForUiProvider, type AiModelSuggestions } from '@/lib/ai-model-suggestions';
import { fetchLiveModelRouterModels, filterRouterModels } from '@/lib/modelrouter-models';
import { cn } from '@/lib/utils';
import type {
  AIAgent,
  ApiResponse,
  AssistantThread,
  AssistantThreadFolder,
  AIAgentStatus,
  AssistantThreadShare,
  ResearchRun,
  ResearchFocusArea,
} from '@/types';
import { PlannerReviewPanel } from '@/components/ai/PlannerReviewPanel';
import { ResearchWorkspace } from '@/components/research/ResearchWorkspace';
import { loadResearchUiDefaults } from '@/lib/research-ui-defaults';
import { getHelpHashWithArticle } from '@/lib/page-help';
import { getConnectionLabel, normalizeTestMessage } from './status-utils';
import { MarkdownText } from '@/components/common/MarkdownText';
import { formatAssistantDisplayMarkdown } from '@/lib/format-research-summary';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Bot, Plus, Settings, Sparkles, Send, Loader2, Copy, Check,
  Cpu, Globe, Server, Trash2, Play, X, Zap, MessageSquare,
  FileText, Languages, Wand2, MoreVertical, Link as LinkIcon, ShieldCheck,
  Info, Star, Archive, ArchiveRestore, FolderPlus, Pencil, HelpCircle,
  Folder, ChevronDown, ChevronRight, Share2, FileDown, SquarePen, Inbox, FolderInput, Users,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const PROVIDERS = [
  { value: 'openai', label: 'OpenAI', icon: <Sparkles size={16} />, color: 'text-success' },
  { value: 'azure', label: 'Azure OpenAI', icon: <Globe size={16} />, color: 'text-accent' },
  { value: 'modelrouter', label: 'Model Router', icon: <Cpu size={16} />, color: 'text-accent' },
  { value: 'ollama', label: 'Ollama (Lokal)', icon: <Server size={16} />, color: 'text-muted' },
  { value: 'minimax', label: 'MiniMax (OpenAI-kompatibel)', icon: <Cpu size={16} />, color: 'text-warning' },
  { value: 'zai', label: 'ZAI (OpenAI-kompatibel)', icon: <Cpu size={16} />, color: 'text-danger' },
];

function companionProviderLabel(provider: string): string {
  if (provider === 'azure') return 'Azure OpenAI';
  if (provider === 'modelrouter') return 'Model Router';
  if (provider === 'ollama') return 'Ollama';
  if (provider === 'minimax') return 'MiniMax';
  if (provider === 'zai') return 'ZAI';
  return 'OpenAI';
}

const QUICK_ACTIONS = [
  { id: 'generate', label: 'Text generieren', icon: <Wand2 size={16} />, desc: 'Marketingtext, E-Mail, Social Post erstellen' },
  { id: 'improve', label: 'Text verbessern', icon: <FileText size={16} />, desc: 'Bestehenden Text optimieren' },
  { id: 'translate', label: 'Übersetzen', icon: <Languages size={16} />, desc: 'Inhalt in andere Sprachen übersetzen' },
  { id: 'chat', label: 'Chat', icon: <MessageSquare size={16} />, desc: 'Freie Konversation mit KI-Assistent' },
  { id: 'research', label: 'Deep Research', icon: <Globe size={16} />, desc: 'Markt und Competitor Recherchen mit Quellen' },
];

/** Kurzbeschreibungen für Capability-Tags (Tooltips) */
const CAPABILITY_HINTS: Record<string, string> = {
  content_generation: 'Marketingtexte, Posts und E-Mails erstellen',
  translation: 'Inhalte in andere Sprachen übersetzen',
  improvement: 'Bestehende Texte überarbeiten und klarer formulieren',
  data_analysis: 'Kennzahlen und Daten interpretieren',
  reporting: 'Zusammenfassungen und Berichte aus Daten',
  campaign_planning: 'Kampagnen strukturieren und planen',
  ab_testing: 'Tests und Varianten für Kampagnen',
};

const AGENTS_TAB_EXPLANATION =
  'KI-Agenten sind gespeicherte Profile (Provider, Modell, Fähigkeiten). Unter «Agent bearbeiten» legen Sie pro Agent das Modell fest — z. B. günstigeres Modell für einfache Aufgaben, stärkeres für komplexe. Über «Zum Chat» nutzt der Companion genau dieses Profil (Modell und ggf. Endpunkt aus der Konfiguration). «Verbindung testen» prüft dieses Profil. «Standard-KI» im Chat wechselt zurück zur globalen KI aus den Einstellungen.';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isPersistedAgentId(id: string): boolean {
  return UUID_RE.test(id);
}

const MODEL_FIELD_HINT =
  'Vorschläge aus Einstellungen (Provider Credentials, KI-Defaults, bestehende Agenten). Keine Live-Abfrage beim Provider. Freie Eingabe möglich. Azure: Deployment-Name exakt wie in Azure.';

const AI_AGENT_MODEL_PRESETS = ['auto', 'GLM-4.7-Flash', 'GLM-5', 'gpt-4o', 'gpt-4o-mini'] as const;

type AgentLinkHealth = 'untested' | 'ok' | 'error';

const LINK_HEALTH_STORAGE_KEY = 'lc_ai_agent_link_health_v1';

function loadPersistedLinkHealth(): Record<string, AgentLinkHealth> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(LINK_HEALTH_STORAGE_KEY);
    if (!raw) return {};
    const o = JSON.parse(raw) as Record<string, string>;
    const out: Record<string, AgentLinkHealth> = {};
    for (const [k, v] of Object.entries(o)) {
      if (v === 'ok' || v === 'error') out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function persistLinkHealthMap(map: Record<string, AgentLinkHealth>) {
  if (typeof window === 'undefined') return;
  try {
    const slim: Record<string, 'ok' | 'error'> = {};
    for (const [k, v] of Object.entries(map)) {
      if (v === 'ok' || v === 'error') slim[k] = v;
    }
    sessionStorage.setItem(LINK_HEALTH_STORAGE_KEY, JSON.stringify(slim));
  } catch {
    /* ignore quota / private mode */
  }
}

function mergeLinkHealthForAgents(
  agentsList: AIAgent[],
  prev: Record<string, AgentLinkHealth>
): Record<string, AgentLinkHealth> {
  const next: Record<string, AgentLinkHealth> = {};
  for (const a of agentsList) {
    next[a.id] = prev[a.id] ?? 'untested';
  }
  // persist so removed IDs disappear from storage on next test
  persistLinkHealthMap(next);
  return next;
}

function agentDotClass(isActive: boolean, health: AgentLinkHealth): string {
  if (!isActive) return 'bg-border dark:bg-muted';
  if (health === 'ok') return 'bg-success';
  if (health === 'error') return 'bg-danger';
  return 'bg-warning';
}

function agentDotAriaLabel(isActive: boolean, health: AgentLinkHealth): string {
  if (!isActive) return 'Profil deaktiviert';
  if (health === 'ok') return 'Letzter Verbindungstest erfolgreich';
  if (health === 'error') return 'Letzter Verbindungstest fehlgeschlagen';
  return 'Verbindung noch nicht geprüft';
}

/** Fallback, wenn kein `description` in der Datenbank hinterlegt ist. */
function deriveAgentGoalFromCapabilities(agent: Pick<AIAgent, 'capabilities'>): string {
  const caps = agent.capabilities ?? [];
  if (caps.length === 0) {
    return 'Allgemeiner Marketing-Companion: Fragen beantworten und Aufgaben mit dem gewählten Modell unterstützen.';
  }
  const hints = caps.map((c) => CAPABILITY_HINTS[c] ?? c.replace(/_/g, ' '));
  return `Ausgelegt auf: ${hints.join(' · ')}. «Zum Chat» startet die Konversation mit genau diesem Profil und Modell.`;
}

function getAgentGoalText(agent: AIAgent): string {
  const d = agent.description?.trim();
  if (d) return d;
  return deriveAgentGoalFromCapabilities(agent);
}

const CHAT_EXAMPLES = [
  'Erstelle eine neue Kampagne fuer Q2 inkl. Budget und Kanalvorschlag.',
  'Lege einen Kalender-Reminder fuer das Kampagnen-Review in 7 Tagen an.',
  'Fuege ein weiteres Teammitglied zum Team Digital Marketing hinzu.',
  'Erstelle einen neuen Zulieferer mit Kontakt-E-Mail und Notizen.',
  'Starte eine Google-Ads Kampagne als Dry-Run mit 1500 CHF Budget.',
];

const STRATEGY_WIZARD_HANDOFF_KEY = 'lc_strategy_wizard_handoff_v1';
const STRATEGY_WIZARD_HANDOFF_LOCK = 'lc_strategy_wizard_handoff_v1_lock';

function stripWizardQueryFromHash() {
  const h = window.location.hash;
  if (!h.includes('?')) return;
  const base = h.split('?')[0];
  window.location.hash = base && base.length > 1 ? base : '#/companion';
}

function stripAgentQueryFromHash() {
  const raw = window.location.hash.slice(1) || '';
  const qIdx = raw.indexOf('?');
  if (qIdx < 0) return;
  const pathRaw = raw.slice(0, qIdx) || '/';
  const path = pathRaw.startsWith('/') ? pathRaw : `/${pathRaw}`;
  const qs = raw.slice(qIdx + 1);
  const params = new URLSearchParams(qs);
  if (!params.has('agent')) return;
  params.delete('agent');
  const next = params.toString();
  window.location.hash = next ? `${path}?${next}` : path;
}

function buildStrategyWizardHandoffMessage(payload: {
  title: string;
  year: number;
  wizardStep: string;
  completedRuns: number;
  recentQueries: string[];
  userNote?: string;
}): string {
  const stepLabels: Record<string, string> = {
    confirm: 'Ausgangslage',
    research_prepare: 'Forschung planen',
    research_review: 'Forschung auswerten',
    strategy_refine: 'Strategie ausarbeiten',
  };
  const stepLabel = stepLabels[payload.wizardStep] ?? payload.wizardStep;
  const lines = (payload.recentQueries ?? [])
    .filter((q) => q && String(q).trim())
    .slice(0, 8)
    .map((q) => `- ${String(q).trim()}`);
  const rq = lines.length > 0 ? lines.join('\n') : '- (noch keine)';
  const note = payload.userNote?.trim();
  return [
    'Ich komme vom Strategie-Assistenten in der Marketingstrategie.',
    '',
    `Strategie: ${payload.title} (${payload.year})`,
    `Aktueller Schritt: ${stepLabel}`,
    `Abgeschlossene Research-Runs: ${payload.completedRuns}`,
    '',
    'Letzte Research-Fragen (Auszug):',
    rq,
    note ? `\nMein Zusatz:\n${note}\n` : '',
    'Bitte hilf mir beim nächsten Schritt: konkrete Empfehlungen, offene Punkte und Priorisierung.',
  ].join('\n');
}

interface ChatMsg {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  actions?: Array<{ tool: string; dryRun: boolean; input?: Record<string, unknown>; result?: Record<string, unknown> }>;
}

export function AIAssistantsPage() {
  const [agents, setAgents] = useState<AIAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [agentsError, setAgentsError] = useState<string | null>(null);
  const [createAgentError, setCreateAgentError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'chat' | 'agents' | 'generate' | 'improve' | 'translate' | 'research' | 'status'>('chat');
  const [threads, setThreads] = useState<AssistantThread[]>([]);
  const [threadListMode, setThreadListMode] = useState<'active' | 'archived'>('active');
  const [folders, setFolders] = useState<AssistantThreadFolder[]>([]);
  const [newFolderName, setNewFolderName] = useState('');
  const [folderBusy, setFolderBusy] = useState(false);
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  const [renamingFolderName, setRenamingFolderName] = useState('');
  /** Eingeklappte Projekt-Ordner (Sidebar, ChatGPT-Style). */
  const [collapsedFolderIds, setCollapsedFolderIds] = useState<Set<string>>(() => new Set());
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [renamingThreadId, setRenamingThreadId] = useState<string | null>(null);
  const [renamingTitle, setRenamingTitle] = useState('');
  const [renamingBusy, setRenamingBusy] = useState(false);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [executingActionKey, setExecutingActionKey] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [genInput, setGenInput] = useState('');
  const [genOutput, setGenOutput] = useState('');
  const [genLoading, setGenLoading] = useState(false);
  const [improveInput, setImproveInput] = useState('');
  const [improveOutput, setImproveOutput] = useState('');
  const [improveLoading, setImproveLoading] = useState(false);
  const [translateInput, setTranslateInput] = useState('');
  const [translateLang, setTranslateLang] = useState('en');
  const [translateOutput, setTranslateOutput] = useState('');
  const [translateLoading, setTranslateLoading] = useState(false);
  const [researchInput, setResearchInput] = useState('');
  const [researchRuns, setResearchRuns] = useState<ResearchRun[]>([]);
  const [researchFocusArea, setResearchFocusArea] = useState<ResearchFocusArea>('market');
  const [researchLoading, setResearchLoading] = useState(false);
  const [researchProvider, setResearchProvider] = useState<'openai' | 'azure_cognitive_services' | 'azure_foundry' | 'custom_o3gpt' | 'modelrouter'>('openai');
  const [statusData, setStatusData] = useState<AIAgentStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shares, setShares] = useState<AssistantThreadShare[]>([]);
  const [shareEmail, setShareEmail] = useState('');
  const [shareAccess, setShareAccess] = useState<'view' | 'edit'>('view');
  const [sharingBusy, setSharingBusy] = useState(false);
  const [agentTestResult, setAgentTestResult] = useState<Record<string, string>>({});
  /** Letzter Verbindungstest pro Agent (nicht dasselbe wie is_active). */
  const [agentLinkHealth, setAgentLinkHealth] = useState<Record<string, AgentLinkHealth>>(loadPersistedLinkHealth);
  /** Nach «Zum Chat» vom Agenten-Tab: Hinweis-Banner im Chat (Profil vs. Standard-KI). */
  const [agentChatHintName, setAgentChatHintName] = useState<string | null>(null);
  /** Wenn gesetzt, nutzt /ai/chat dieses Agenten-Profil (resolveChatAIConfig). */
  const [chatAgentId, setChatAgentId] = useState<string | null>(null);
  const [plannerRefreshVersion, setPlannerRefreshVersion] = useState(0);
  const [newAgent, setNewAgent] = useState({ name: '', provider: 'openai', model: 'gpt-4o', capabilities: '', description: '' });
  const [agentModelSuggestions, setAgentModelSuggestions] = useState<AiModelSuggestions | null>(null);
  const [routerLiveModels, setRouterLiveModels] = useState<string[]>([]);
  const [editAgent, setEditAgent] = useState<AIAgent | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    provider: 'openai' as AIAgent['provider'],
    model: '',
    capabilities: '',
    description: '',
    is_active: true,
  });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const createModelQuickPick = useMemo(() => {
    const base = mergeModelOptionsForUiProvider(newAgent.provider, agentModelSuggestions, AI_AGENT_MODEL_PRESETS);
    if (newAgent.provider === 'modelrouter' && routerLiveModels.length) {
      return [...new Set([...base, ...routerLiveModels])].sort((a, b) => a.localeCompare(b));
    }
    return base;
  }, [newAgent.provider, agentModelSuggestions, routerLiveModels]);
  const editModelQuickPick = useMemo(() => {
    const base = mergeModelOptionsForUiProvider(editForm.provider, agentModelSuggestions, AI_AGENT_MODEL_PRESETS);
    if (editForm.provider === 'modelrouter' && routerLiveModels.length) {
      return [...new Set([...base, ...routerLiveModels])].sort((a, b) => a.localeCompare(b));
    }
    return base;
  }, [editForm.provider, agentModelSuggestions, routerLiveModels]);

  const endRef = useRef<HTMLDivElement>(null);
  /** Verhindert, dass eine langsame API-Antwort einen neueren Thread überschreibt. */
  const threadLoadSeqRef = useRef(0);

  useEffect(() => { loadAgents(); }, []);

  useEffect(() => {
    if (!showCreate && !editAgent) return;
    void api
      .get<{ success?: boolean; data?: AiModelSuggestions }>('/ai/model-suggestions')
      .then((res) => {
        if (res.success !== false && res.data) setAgentModelSuggestions(res.data);
        else setAgentModelSuggestions(null);
      })
      .catch(() => setAgentModelSuggestions(null));
  }, [showCreate, editAgent]);

  useEffect(() => {
    const p = editAgent ? editForm.provider : showCreate ? newAgent.provider : null;
    if (p !== 'modelrouter') return;
    void fetchLiveModelRouterModels()
      .then((models) => setRouterLiveModels(filterRouterModels(models, 'chat').map((m) => m.id)))
      .catch(() => setRouterLiveModels([]));
  }, [showCreate, editAgent, newAgent.provider, editForm.provider]);
  useEffect(() => {
    void loadResearchUiDefaults().then((d) => {
      if (!d) return;
      setResearchFocusArea(d.focus_area);
      setResearchProvider(d.provider);
    });
  }, []);
  useEffect(() => { void loadStatus(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadPendingPlannerApprovals = useCallback(async () => setPlannerRefreshVersion((value) => value + 1), []);

  useEffect(() => {
    if (activeTab === 'chat') void loadPendingPlannerApprovals();
  }, [activeTab, loadPendingPlannerApprovals]);

  const fetchResearchRuns = async () => {
    try {
      const res = await api.get<
        ApiResponse<ResearchRun[]> & { total?: number; page?: number; limit?: number }
      >('/ai/research-runs?limit=100');
      if (res.success && Array.isArray(res.data)) setResearchRuns(res.data);
    } catch {
      setResearchRuns([]);
    }
  };

  useEffect(() => {
    if (activeTab === 'research') void fetchResearchRuns();
  }, [activeTab]);

  const loadAgents = async () => {
    setLoading(true);
    setAgentsError(null);
    try {
      const res = await api.get<ApiResponse<AIAgent[]>>('/ai/agents');
      if (res.success && Array.isArray(res.data)) {
        const list = res.data;
        setAgents(list);
        setAgentLinkHealth((prev) => mergeLinkHealthForAgents(list, prev));
      } else {
        throw new Error(res.error || 'Agenten konnten nicht geladen werden.');
      }
    } catch (error) {
      setAgents([]);
      setAgentsError(error instanceof Error ? error.message : 'Agenten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = useCallback(async () => {
    try {
      const res = await api.get<ApiResponse<AssistantThreadFolder[]>>('/ai/thread-folders');
      if (res.success && res.data) setFolders(res.data);
    } catch {
      setFolders([]);
    }
  }, []);

  useEffect(() => { void loadFolders(); }, [loadFolders]);

  const loadThreads = useCallback(async () => {
    try {
      const res = await api.get<ApiResponse<AssistantThread[]>>(
        `/ai/threads?list_mode=${threadListMode}&limit=100`
      );
      if (res.success && res.data) setThreads(res.data);
    } catch {
      setThreads([]);
    }
  }, [threadListMode]);

  useEffect(() => {
    void loadThreads();
  }, [loadThreads]);

  const threadSections = useMemo(() => {
    const NONE = '__none__';
    const byFolder = new Map<string, AssistantThread[]>();
    for (const t of threads) {
      const k = t.folder_id ?? NONE;
      if (!byFolder.has(k)) byFolder.set(k, []);
      byFolder.get(k)!.push(t);
    }
    const sortFn = (a: AssistantThread, b: AssistantThread) => {
      const sd = (b.is_starred ? 1 : 0) - (a.is_starred ? 1 : 0);
      if (sd !== 0) return sd;
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    };
    for (const arr of byFolder.values()) arr.sort(sortFn);
    const sortedFolders = [...folders].sort(
      (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)
    );
    return { byFolder, sortedFolders, NONE };
  }, [threads, folders]);

  const canManageThread = (t: AssistantThread) => t.shared_access === 'owner';

  const toggleFolderCollapse = (folderId: string) => {
    setCollapsedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name || folderBusy) return;
    setFolderBusy(true);
    try {
      await api.post('/ai/thread-folders', { name });
      setNewFolderName('');
      await loadFolders();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Ordner konnte nicht erstellt werden.');
    } finally {
      setFolderBusy(false);
    }
  };

  const submitRenameFolder = async (folderId: string) => {
    const name = renamingFolderName.trim();
    if (!name) return;
    setFolderBusy(true);
    try {
      await api.patch(`/ai/thread-folders/${folderId}`, { name });
      setRenamingFolderId(null);
      setRenamingFolderName('');
      await loadFolders();
      await loadThreads();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Ordner konnte nicht umbenannt werden.');
    } finally {
      setFolderBusy(false);
    }
  };

  const deleteFolder = async (folderId: string) => {
    if (!window.confirm('Ordner löschen? Threads werden in «Ohne Ordner» verschoben.')) return;
    setFolderBusy(true);
    try {
      await api.delete(`/ai/thread-folders/${folderId}`);
      await loadFolders();
      await loadThreads();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Ordner konnte nicht gelöscht werden.');
    } finally {
      setFolderBusy(false);
    }
  };

  const createThreadInFolder = async (folderId: string | null) => {
    const seq = ++threadLoadSeqRef.current;
    try {
      const res = await api.post<ApiResponse<{ id: string }>>('/ai/threads', {
        title: 'Neue Unterhaltung', folder_id: folderId,
      });
      await loadThreads();
      if (!res.data?.id || seq !== threadLoadSeqRef.current) return;
      setActiveThreadId(res.data.id);
      setMessages([]);
      setInput('');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Chat konnte nicht erstellt werden.');
    }
  };

  const toggleStarThread = async (t: AssistantThread) => {
    if (!canManageThread(t)) return;
    try {
      await api.patch(`/ai/threads/${t.id}`, { is_starred: !t.is_starred });
      await loadThreads();
    } catch {
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: 'assistant', content: 'Highlight konnte nicht gespeichert werden.' },
      ]);
    }
  };

  const setThreadArchived = async (t: AssistantThread, archived: boolean) => {
    if (!canManageThread(t)) return;
    const seq = threadLoadSeqRef.current;
    try {
      await api.patch(`/ai/threads/${t.id}`, { status: archived ? 'archived' : 'active' });
      if (activeThreadId === t.id && seq === threadLoadSeqRef.current) {
        threadLoadSeqRef.current += 1;
        setActiveThreadId(null);
        setMessages([]);
      }
      await loadThreads();
    } catch {
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: 'assistant', content: 'Archiv-Status konnte nicht geändert werden.' },
      ]);
    }
  };

  const setThreadFolder = async (threadId: string, folderId: string | null) => {
    const t = threads.find((x) => x.id === threadId);
    if (!t || !canManageThread(t)) return;
    try {
      await api.patch(`/ai/threads/${threadId}`, { folder_id: folderId });
      await loadThreads();
    } catch {
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: 'assistant', content: 'Ordner konnte nicht zugewiesen werden.' },
      ]);
    }
  };

  const loadStatus = async () => {
    setStatusLoading(true);
    try {
      const res = await api.get<ApiResponse<AIAgentStatus>>('/ai/status');
      if (res.success && res.data) setStatusData(res.data);
    } catch (error) {
      setStatusData(null);
      toast.error(error instanceof Error ? error.message : 'KI-Status konnte nicht geladen werden.');
    } finally {
      setStatusLoading(false);
    }
  };

  const testAgent = async (agentId: string) => {
    setAgentTestResult((prev) => ({ ...prev, [agentId]: 'Test läuft…' }));
    try {
      const res = await api.post<ApiResponse<{ connected: boolean; message: string }>>(`/ai/agents/${agentId}/test`);
      const message = normalizeTestMessage(res.data?.message || (res.success ? 'Test erfolgreich' : 'Test fehlgeschlagen'));
      setAgentTestResult((prev) => ({ ...prev, [agentId]: message }));
      const ok = Boolean(res.success && res.data?.connected);
      setAgentLinkHealth((prev) => {
        const status: AgentLinkHealth = ok ? 'ok' : 'error';
        const next: Record<string, AgentLinkHealth> = { ...prev, [agentId]: status };
        persistLinkHealthMap(next);
        return next;
      });
    } catch {
      setAgentTestResult((prev) => ({ ...prev, [agentId]: 'Test fehlgeschlagen' }));
      setAgentLinkHealth((prev) => {
        const next: Record<string, AgentLinkHealth> = { ...prev, [agentId]: 'error' };
        persistLinkHealthMap(next);
        return next;
      });
    }
  };

  const applyThreadAgentFromContext = (threadMeta?: AssistantThread) => {
    const t = threadMeta;
    const raw = t?.context?.chat_agent_id;
    const id = typeof raw === 'string' && isPersistedAgentId(raw) ? raw : null;
    if (id) {
      setChatAgentId(id);
      const agent = agents.find((a) => a.id === id);
      setAgentChatHintName(agent?.name ?? null);
    } else {
      setChatAgentId(null);
      setAgentChatHintName(null);
    }
  };

  const selectThread = async (threadId: string, threadMeta?: AssistantThread) => {
    const seq = ++threadLoadSeqRef.current;
    setActiveThreadId(threadId);
    applyThreadAgentFromContext(threadMeta ?? threads.find((x) => x.id === threadId));
    setMessages([]);
    try {
      const res = await api.get<ApiResponse<Array<{ id: string; role: string; content: string }>>>(
        `/ai/threads/${threadId}/messages`
      );
      if (threadLoadSeqRef.current !== seq) return;
      const rows = Array.isArray(res.data) ? res.data : [];
      const mapped = rows
        .filter((m) => m.role === 'user' || m.role === 'assistant' || m.role === 'tool')
        .map((m) =>
          m.role === 'tool'
            ? { id: m.id, role: 'assistant' as const, content: m.content }
            : { id: m.id, role: m.role as 'user' | 'assistant', content: m.content }
        );
      setMessages(mapped);
    } catch {
      if (threadLoadSeqRef.current === seq) setMessages([]);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash.slice(1) || '';
    const qIdx = hash.indexOf('?');
    const pathRaw = (qIdx >= 0 ? hash.slice(0, qIdx) : hash) || '/';
    const path = pathRaw.startsWith('/') ? pathRaw : `/${pathRaw}`;
    const qs = qIdx >= 0 ? hash.slice(qIdx + 1) : '';
    const params = new URLSearchParams(qs);
    if (path !== '/companion' && path !== '/ai') return;
    if (params.get('wizard') !== 'strategy' || params.get('handoff') !== '1') return;

    if (sessionStorage.getItem(STRATEGY_WIZARD_HANDOFF_LOCK) === '1') return;

    const raw = sessionStorage.getItem(STRATEGY_WIZARD_HANDOFF_KEY);
    if (!raw) {
      stripWizardQueryFromHash();
      return;
    }

    sessionStorage.setItem(STRATEGY_WIZARD_HANDOFF_LOCK, '1');
    sessionStorage.removeItem(STRATEGY_WIZARD_HANDOFF_KEY);

    let payload: {
      title: string;
      year: number;
      wizardStep: string;
      completedRuns: number;
      recentQueries: string[];
      userNote?: string;
    };
    try {
      payload = JSON.parse(raw) as typeof payload;
    } catch {
      sessionStorage.removeItem(STRATEGY_WIZARD_HANDOFF_LOCK);
      stripWizardQueryFromHash();
      return;
    }
    if (!payload || typeof payload.title !== 'string') {
      sessionStorage.removeItem(STRATEGY_WIZARD_HANDOFF_LOCK);
      stripWizardQueryFromHash();
      return;
    }

    void (async () => {
      try {
        setActiveTab('chat');
        setChatAgentId(null);
        setAgentChatHintName(null);

        const threadTitle = `Strategie: ${payload.title} (${payload.year})`;
        const res = await api.post<ApiResponse<{ id: string }>>('/ai/threads', { title: threadTitle });
        const threadId = res.data?.id;
        if (!threadId) return;

        const userMsg = buildStrategyWizardHandoffMessage(payload);
        await api.post<ApiResponse<{ response: string; thread_id?: string }>>('/ai/chat', {
          message: userMsg,
          thread_id: threadId,
          history: [],
        });

        setActiveThreadId(threadId);
        const tr = await api.get<ApiResponse<AssistantThread[]>>(`/ai/threads?list_mode=active&limit=100`);
        if (tr.success && tr.data) setThreads(tr.data);

        const msgRes = await api.get<ApiResponse<Array<{ id: string; role: string; content: string }>>>(
          `/ai/threads/${threadId}/messages`
        );
        if (msgRes.success && msgRes.data) {
          setMessages(
            msgRes.data
              .filter((m) => m.role === 'user' || m.role === 'assistant')
              .map((m) => ({ id: m.id, role: m.role as 'user' | 'assistant', content: m.content }))
          );
        }
      } catch {
        /* Companion bleibt nutzbar */
      } finally {
        sessionStorage.removeItem(STRATEGY_WIZARD_HANDOFF_LOCK);
        stripWizardQueryFromHash();
      }
    })();
  }, []);

  /** Deep link from supplier detail etc.: `#/companion?agent=<uuid>` → Chat tab with that agent profile. */
  useEffect(() => {
    const applyAgentFromHash = () => {
      if (typeof window === 'undefined') return;
      const hash = window.location.hash.slice(1) || '';
      const qIdx = hash.indexOf('?');
      const pathRaw = (qIdx >= 0 ? hash.slice(0, qIdx) : hash) || '/';
      const path = pathRaw.startsWith('/') ? pathRaw : `/${pathRaw}`;
      if (path !== '/companion' && path !== '/ai') return;
      const qs = qIdx >= 0 ? hash.slice(qIdx + 1) : '';
      const params = new URLSearchParams(qs);
      const agentId = params.get('agent');
      if (!agentId || !UUID_RE.test(agentId)) return;
      setActiveTab('chat');
      setChatAgentId(agentId);
      setAgentChatHintName(null);
      stripAgentQueryFromHash();
    };
    applyAgentFromHash();
    window.addEventListener('hashchange', applyAgentFromHash);
    return () => window.removeEventListener('hashchange', applyAgentFromHash);
  }, []);

  useEffect(() => {
    if (!chatAgentId) return;
    const agent = agents.find((a) => a.id === chatAgentId);
    if (agent?.name) setAgentChatHintName(agent.name);
  }, [agents, chatAgentId]);

  const sendChat = async () => {
    if (!input.trim() || sending) return;
    const seq = threadLoadSeqRef.current;
    const userMsg: ChatMsg = { id: crypto.randomUUID(), role: 'user', content: input.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);
    try {
      const res = await api.post<ApiResponse<{ response: string; actions?: Array<{ tool: string; dryRun: boolean; input?: Record<string, unknown>; result?: Record<string, unknown> }> }>>('/ai/chat', {
        message: userMsg.content,
        thread_id: activeThreadId || undefined,
        /** `null` = explizit Standard-KI; UUID = Agent; weglassen würde gespeicherten Thread-Agent nutzen (Backend). */
        agent_id: chatAgentId,
        history: messages.slice(-10).map(m => ({ role: m.role, content: m.content })),
      });
      if (seq !== threadLoadSeqRef.current) {
        void loadThreads();
        return;
      }
      if (!res.success) {
        const detail = res.error_detail?.trim();
        const base = res.error || 'Unbekannter Fehler vom Server.';
        const code = res.error_code ? ` [${res.error_code}]` : '';
        const text = detail && detail !== base ? `${base}\n\n${detail}` : base;
        setMessages(prev => [...prev, {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: `Fehler:${code} ${text}`,
        }]);
        return;
      }
      if (!activeThreadId && (res.data as any)?.thread_id) {
        setActiveThreadId((res.data as any).thread_id as string);
        loadThreads();
      }
      void loadPendingPlannerApprovals();
      const providerInfo = (res.data as any)?.provider ? `\n\n[Provider: ${(res.data as any).provider}${(res.data as any)?.model ? ` / ${(res.data as any).model}` : ''}]` : '';
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(), role: 'assistant',
        content: (res.data?.response || (res.data as any)?.reply || 'Keine Antwort erhalten.') + providerInfo,
        actions: (res.data as any)?.actions || [],
      }]);
    } catch (error) {
      if (seq !== threadLoadSeqRef.current) return;
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(), role: 'assistant',
        content: error instanceof Error ? error.message : 'Fehler bei der Verarbeitung. Bitte erneut versuchen.',
      }]);
    } finally {
      setSending(false);
    }
  };

  const executeSuggestedAction = async (messageId: string, actionIndex: number) => {
    const selectedMessage = messages.find((message) => message.id === messageId);
    const selectedAction = selectedMessage?.actions?.[actionIndex];
    if (!selectedAction || !activeThreadId || selectedAction.dryRun === false) return;

    const actionKey = `${messageId}:${actionIndex}`;
    setExecutingActionKey(actionKey);
    try {
      const res = await api.post<ApiResponse<Record<string, unknown>>>('/ai/actions/execute', {
        thread_id: activeThreadId,
        tool: selectedAction.tool,
        input: selectedAction.input || {},
        dry_run: false,
        ai_agent_id: chatAgentId || undefined,
      });

      setMessages((prev) =>
        prev.map((message) => {
          if (message.id !== messageId || !message.actions) return message;
          return {
            ...message,
            actions: message.actions.map((action, idx) =>
              idx === actionIndex
                ? {
                    ...action,
                    dryRun: false,
                    result: {
                      ...(action.result || {}),
                      execution: res.success ? (res.data || { status: 'queued' }) : { error: res.error || 'Ausfuehrung fehlgeschlagen' },
                    },
                  }
                : action
            ),
          };
        })
      );
    } finally {
      setExecutingActionKey(null);
    }
  };

  const runResearch = async () => {
    if (!researchInput.trim()) return;
    setResearchLoading(true);
    try {
      const res = await api.post<
        ApiResponse<{
          research: { summary: string; confidence: number; sources: Array<{ title: string; url?: string }> };
          run?: Record<string, unknown>;
        }>
      >('/ai/deep-research', {
        query: researchInput,
        provider: researchProvider,
        thread_id: activeThreadId || undefined,
      });
      if (!res.success) {
        return;
      }
      setResearchInput('');
      await fetchResearchRuns();
      loadThreads();
    } catch {
      // Error surfaced via API client / toast in other tabs; list refresh still useful
      await fetchResearchRuns();
    } finally {
      setResearchLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!genInput.trim()) return;
    setGenLoading(true);
    try {
      const res = await api.post<ApiResponse<{ content: string }>>('/ai/generate', { prompt: genInput, type: 'marketing_copy' });
      setGenOutput(res.data?.content || 'Keine Ausgabe erhalten.');
    } catch { setGenOutput('Fehler bei der Generierung.'); }
    finally { setGenLoading(false); }
  };

  const handleImprove = async () => {
    if (!improveInput.trim()) return;
    setImproveLoading(true);
    try {
      const res = await api.post<ApiResponse<{ content: string }>>('/ai/improve', { text: improveInput });
      setImproveOutput(res.data?.content || 'Keine Ausgabe erhalten.');
    } catch { setImproveOutput('Fehler bei der Verbesserung.'); }
    finally { setImproveLoading(false); }
  };

  const handleTranslate = async () => {
    if (!translateInput.trim()) return;
    setTranslateLoading(true);
    try {
      const res = await api.post<ApiResponse<{ content: string }>>('/ai/translate', {
        text: translateInput, target_language: translateLang,
      });
      setTranslateOutput(res.data?.content || 'Keine Ausgabe erhalten.');
    } catch { setTranslateOutput('Fehler bei der Übersetzung.'); }
    finally { setTranslateLoading(false); }
  };

  const handleCreateAgent = async () => {
    setCreateAgentError(null);
    try {
      const response = await api.post<ApiResponse<AIAgent>>('/ai/agents', {
        name: newAgent.name,
        provider: newAgent.provider,
        model: newAgent.model,
        capabilities: newAgent.capabilities.split(',').map(c => c.trim()).filter(Boolean),
        description: newAgent.description.trim() ? newAgent.description.trim() : null,
      });
      if (!response.success) throw new Error(response.error || 'Agent konnte nicht erstellt werden.');
      await loadAgents();
      setShowCreate(false);
      setNewAgent({ name: '', provider: 'openai', model: 'gpt-4o', capabilities: '', description: '' });
    } catch (error) { setCreateAgentError(error instanceof Error ? error.message : 'Agent konnte nicht erstellt werden.'); }
  };

  const openEditAgent = (agent: AIAgent) => {
    setEditAgent(agent);
    setEditForm({
      name: agent.name,
      provider: agent.provider,
      model: agent.model,
      capabilities: agent.capabilities.join(', '),
      description: agent.description?.trim() ?? '',
      is_active: agent.is_active,
    });
    setEditError(null);
  };

  const handleUpdateAgent = async () => {
    if (!editAgent || !isPersistedAgentId(editAgent.id)) return;
    const name = editForm.name.trim();
    const model = editForm.model.trim();
    if (!name || !model) {
      setEditError('Name und Modell sind erforderlich.');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      const res = await api.patch<ApiResponse<AIAgent>>(`/ai/agents/${editAgent.id}`, {
        name,
        provider: editForm.provider,
        model,
        capabilities: editForm.capabilities.split(',').map((c) => c.trim()).filter(Boolean),
        description: editForm.description.trim() ? editForm.description.trim() : null,
        is_active: editForm.is_active,
      });
      if (!res.success) {
        setEditError(res.error || 'Speichern fehlgeschlagen');
        return;
      }
      await loadAgents();
      if (chatAgentId === editAgent.id && res.data?.name) {
        setAgentChatHintName(res.data.name);
      }
      if (!editForm.is_active && chatAgentId === editAgent.id) {
        setChatAgentId(null);
        setAgentChatHintName(null);
      }
      setEditAgent(null);
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setEditSaving(false);
    }
  };

  const copyText = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const startNewThread = async () => {
    await createThreadInFolder(null);
  };

  const exportThreadPdf = async () => {
    if (!activeThreadId) return;
    setExportingPdf(true);
    try {
      const blob = await api.getBlob(`/export/assistant-thread/${activeThreadId}/pdf`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assistant-thread-${activeThreadId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: 'assistant', content: 'PDF-Export fehlgeschlagen. Bitte erneut versuchen.' },
      ]);
    } finally {
      setExportingPdf(false);
    }
  };

  const openShareModal = async () => {
    if (!activeThreadId) return;
    setShowShareModal(true);
    setSharingBusy(true);
    try {
      const res = await api.get<ApiResponse<AssistantThreadShare[]>>(`/ai/threads/${activeThreadId}/shares`);
      setShares(res.data ?? []);
    } catch {
      setShares([]);
    } finally {
      setSharingBusy(false);
    }
  };

  const shareCurrentThread = async () => {
    if (!activeThreadId || !shareEmail.trim()) return;
    setSharingBusy(true);
    try {
      await api.post(`/ai/threads/${activeThreadId}/share`, {
        target_email: shareEmail.trim(),
        access_level: shareAccess,
      });
      setShareEmail('');
      const refreshed = await api.get<ApiResponse<AssistantThreadShare[]>>(`/ai/threads/${activeThreadId}/shares`);
      setShares(refreshed.data ?? []);
    } catch {
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: 'assistant', content: 'Teilen fehlgeschlagen. Bitte E-Mail prüfen.' },
      ]);
    } finally {
      setSharingBusy(false);
    }
  };

  const revokeShare = async (shareId: string) => {
    if (!activeThreadId) return;
    setSharingBusy(true);
    try {
      await api.delete(`/ai/threads/${activeThreadId}/shares/${shareId}`);
      const refreshed = await api.get<ApiResponse<AssistantThreadShare[]>>(`/ai/threads/${activeThreadId}/shares`);
      setShares(refreshed.data ?? []);
    } finally {
      setSharingBusy(false);
    }
  };

  const startRenamingThread = (thread: AssistantThread) => {
    setRenamingThreadId(thread.id);
    setRenamingTitle(thread.title || '');
  };

  const cancelRenamingThread = () => {
    setRenamingThreadId(null);
    setRenamingTitle('');
  };

  const submitThreadRename = async (threadId: string) => {
    const title = renamingTitle.trim();
    if (!title || renamingBusy) return;
    setRenamingBusy(true);
    try {
      await api.patch(`/ai/threads/${threadId}`, { title });
      setThreads((prev) => prev.map((thread) => (thread.id === threadId ? { ...thread, title } : thread)));
      setRenamingThreadId(null);
      setRenamingTitle('');
    } catch {
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: 'assistant', content: 'Thread konnte nicht umbenannt werden.' },
      ]);
    } finally {
      setRenamingBusy(false);
    }
  };

  const renderThreadRow = (t: AssistantThread) => {
    const manage = canManageThread(t);
    if (renamingThreadId === t.id) {
      return (
        <div
          key={t.id}
          className="flex h-9 max-w-full items-center gap-1 rounded-lg border border-border/40 bg-card-hover/90 px-1.5 dark:bg-card/80"
        >
          <input
            value={renamingTitle}
            onChange={(e) => setRenamingTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submitThreadRename(t.id);
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                cancelRenamingThread();
              }
            }}
            className="min-w-0 flex-1 bg-transparent text-[13px] text-foreground outline-none placeholder:text-muted"
            placeholder="Titel…"
            autoFocus
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => submitThreadRename(t.id)}
                disabled={renamingBusy || !renamingTitle.trim()}
                className="rounded p-1 text-success hover:bg-success/10 disabled:opacity-40"
              >
                <Check className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">Speichern</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={cancelRenamingThread}
                disabled={renamingBusy}
                className="rounded p-1 text-muted hover:bg-card-hover/80 dark:hover:bg-card-hover"
              >
                <X className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">Abbrechen</TooltipContent>
          </Tooltip>
        </div>
      );
    }

    return (
      <div
        key={t.id}
        onClick={() => void selectThread(t.id, t)}
        className={cn(
          'group/thread relative flex h-9 max-w-full cursor-pointer items-center gap-0.5 rounded-lg px-1 transition-colors',
          activeThreadId === t.id
            ? 'bg-primary/12 ring-1 ring-border/35 dark:bg-muted/15'
            : 'hover:bg-card-hover/70 dark:hover:bg-card-hover/90'
        )}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void toggleStarThread(t);
              }}
              disabled={!manage}
              className={cn(
                'shrink-0 rounded-md p-1',
                manage
                  ? 'text-warning hover:bg-warning/10'
                  : 'cursor-not-allowed opacity-30'
              )}
              aria-label={t.is_starred ? 'Highlight entfernen' : 'Hervorheben'}
            >
              <Star className={cn('h-3.5 w-3.5', t.is_starred && 'fill-warning text-warning')} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">{t.is_starred ? 'Highlight entfernen' : 'Hervorheben'}</TooltipContent>
        </Tooltip>

        <div className="flex min-w-0 flex-1 items-center gap-1 text-left">
          {t.is_shared && (
            <span title={`Geteilt (${t.shared_access})`}>
              <Users className="h-3 w-3 shrink-0 text-muted" aria-hidden />
            </span>
          )}
          <span className="min-w-0 flex-1 truncate text-[13px] leading-tight text-foreground">
            {t.title}
          </span>
        </div>

        {manage && (
          <div
            className={cn(
              'flex shrink-0 items-center gap-0.5',
              'opacity-100 max-md:opacity-100 md:pointer-events-none md:opacity-0 md:group-hover/thread:pointer-events-auto md:group-hover/thread:opacity-100 md:group-focus-within/thread:pointer-events-auto md:group-focus-within/thread:opacity-100'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  title="In Projekt verschieben"
                  className="rounded-md p-1 text-muted hover:bg-border/60 hover:text-foreground dark:hover:bg-card-hover"
                  aria-label="In Projekt verschieben"
                >
                  <FolderInput className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 max-h-72 overflow-y-auto">
                <DropdownMenuItem
                  onClick={() => void setThreadFolder(t.id, null)}
                  className="gap-2 text-xs"
                >
                  <Inbox className="h-4 w-4 shrink-0 text-muted" />
                  <span className="flex-1">Ohne Zuordnung</span>
                  {!t.folder_id ? <Check className="h-4 w-4 shrink-0 text-foreground" /> : null}
                </DropdownMenuItem>
                {folders.map((f) => (
                  <DropdownMenuItem
                    key={f.id}
                    onClick={() => void setThreadFolder(t.id, f.id)}
                    className="gap-2 text-xs"
                  >
                    <Folder className="h-4 w-4 shrink-0 text-accent" />
                    <span className="flex-1 truncate">{f.name}</span>
                    {t.folder_id === f.id ? <Check className="h-4 w-4 shrink-0 text-foreground" /> : null}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    startRenamingThread(t);
                  }}
                  className="rounded-md p-1 text-muted hover:bg-border/60 hover:text-foreground dark:hover:bg-card-hover"
                  aria-label="Umbenennen"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">Umbenennen</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void setThreadArchived(t, threadListMode === 'active');
                  }}
                  className="rounded-md p-1 text-muted hover:bg-border/60 hover:text-foreground dark:hover:bg-card-hover"
                  aria-label={threadListMode === 'active' ? 'Archivieren' : 'Wiederherstellen'}
                >
                  {threadListMode === 'active' ? (
                    <Archive className="h-3.5 w-3.5" />
                  ) : (
                    <ArchiveRestore className="h-3.5 w-3.5" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {threadListMode === 'active' ? 'Archivieren' : 'Wiederherstellen'}
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
    );
  };

  return (
    <TooltipProvider delayDuration={250}>
    <div className="space-y-6">
      {agentsError && (
        <div role="alert" className="rounded-lg border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          <p>{agentsError}</p>
          <Button variant="outline" size="sm" className="mt-2" disabled={loading} onClick={() => void loadAgents()}>Agenten erneut laden</Button>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">KI-Assistenten</h1>
        </div>
        <a
          href={`#${getHelpHashWithArticle('companion-ki-assistent')}`}
          className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-muted hover:bg-background dark:hover:bg-card-hover"
        >
          <LinkIcon size={14} />
          Hilfe zu Companion
        </a>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border">
        {QUICK_ACTIONS.map(a => (
          <button
            key={a.id}
            onClick={() => setActiveTab(a.id as any)}
            className={cn(
              'flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors',
              activeTab === a.id
                ? 'border-border text-foreground dark:text-muted'
                : 'border-transparent text-muted hover:text-foreground'
            )}
          >
            {a.icon} {a.label}
          </button>
        ))}
        <button
          onClick={() => setActiveTab('agents')}
          className={cn(
            'flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors',
            activeTab === 'agents'
              ? 'border-border text-foreground'
              : 'border-transparent text-muted hover:text-foreground'
          )}
        >
          <Cpu size={16} /> Agenten
        </button>
        <button
          onClick={() => { setActiveTab('status'); loadStatus(); }}
          className={cn(
            'flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors',
            activeTab === 'status'
              ? 'border-border text-foreground'
              : 'border-transparent text-muted hover:text-foreground'
          )}
        >
          <ShieldCheck size={16} /> Status
        </button>
      </div>

      {activeTab === 'chat' && (
        <div className="grid min-h-0 min-w-0 grid-cols-1 gap-4 lg:grid-cols-[minmax(280px,360px)_minmax(0,3fr)]">
          <div className="flex min-h-0 min-w-0 w-full flex-col rounded-xl border border-border/90 bg-card p-2.5 shadow-sm">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">
                Chats
              </h3>
              <div className="flex items-center gap-0.5">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={exportThreadPdf}
                      disabled={!activeThreadId || exportingPdf}
                      className="rounded-lg p-1.5 text-muted hover:bg-card-hover/80 hover:text-foreground disabled:opacity-40 dark:hover:bg-card-hover"
                      aria-label="PDF exportieren"
                    >
                      {exportingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">PDF exportieren</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={openShareModal}
                      disabled={!activeThreadId}
                      className="rounded-lg p-1.5 text-muted hover:bg-card-hover/80 hover:text-foreground disabled:opacity-40 dark:hover:bg-card-hover"
                      aria-label="Teilen"
                    >
                      <Share2 className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Teilen</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => void startNewThread()}
                      className="rounded-lg p-1.5 text-muted hover:bg-card-hover/80 hover:text-foreground dark:hover:bg-card-hover dark:hover:text-muted"
                      aria-label="Neuer Chat"
                    >
                      <SquarePen className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Neuer Chat</TooltipContent>
                </Tooltip>
              </div>
            </div>
            <div className="mb-2 flex rounded-lg border border-border/80 p-0.5 dark:border-border">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      'flex flex-1 items-center justify-center rounded-md py-1.5 transition-colors',
                      threadListMode === 'active'
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted hover:bg-card-hover'
                    )}
                    onClick={() => setThreadListMode('active')}
                    aria-label="Aktive Chats"
                  >
                    <Inbox className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Aktive Chats</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      'flex flex-1 items-center justify-center rounded-md py-1.5 transition-colors',
                      threadListMode === 'archived'
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted hover:bg-card-hover'
                    )}
                    onClick={() => setThreadListMode('archived')}
                    aria-label="Archiv"
                  >
                    <Archive className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Archiv</TooltipContent>
              </Tooltip>
            </div>
            <div className="mb-2 flex w-full items-center gap-1 rounded-lg border border-border/80 bg-background/80 px-2 py-1 dark:border-border dark:bg-card/50">
              <FolderPlus className="h-3.5 w-3.5 shrink-0 text-accent/90" aria-hidden />
              <input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !folderBusy && newFolderName.trim()) void createFolder();
                }}
                placeholder="Neues Projekt…"
                className="min-w-0 flex-1 bg-transparent text-[12px] text-foreground outline-none placeholder:text-muted"
              />
              <button
                type="button"
                className="rounded-md p-1 text-accent hover:bg-accent/10 disabled:opacity-40"
                onClick={() => void createFolder()}
                disabled={folderBusy || !newFolderName.trim()}
                title="Projekt anlegen"
                aria-label="Projekt anlegen"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-0.5">
              {threads.length === 0 && folders.length === 0 ? (
                <p className="px-1 py-4 text-center text-xs text-muted">
                  {threadListMode === 'active' ? 'Keine aktiven Chats.' : 'Keine archivierten Chats.'}
                </p>
              ) : (
                <>
                  {folders.length > 0 && (
                    <p className="px-1 text-[10px] font-semibold uppercase tracking-wider text-muted">
                      Projekte
                    </p>
                  )}
                  {threadSections.sortedFolders.map((folder) => {
                    const list = threadSections.byFolder.get(folder.id) ?? [];
                    const collapsed = collapsedFolderIds.has(folder.id);
                    return (
                      <div key={folder.id} className="group/fwrap rounded-lg border border-border/70 bg-background/50 dark:border-border/80 dark:bg-card/35">
                        <div className="flex items-center gap-0.5 px-1 py-1">
                          <button
                            type="button"
                            className="rounded p-0.5 text-muted hover:bg-card-hover/80 dark:hover:bg-card-hover"
                            onClick={() => toggleFolderCollapse(folder.id)}
                            title={collapsed ? 'Aufklappen' : 'Einklappen'}
                            aria-expanded={!collapsed}
                          >
                            {collapsed ? (
                              <ChevronRight className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </button>
                          {renamingFolderId === folder.id ? (
                            <div className="flex min-w-0 flex-1 items-center gap-1">
                              <Folder className="h-4 w-4 shrink-0 text-accent" aria-hidden />
                              <input
                                value={renamingFolderName}
                                onChange={(e) => setRenamingFolderName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') void submitRenameFolder(folder.id);
                                  if (e.key === 'Escape') {
                                    setRenamingFolderId(null);
                                    setRenamingFolderName('');
                                  }
                                }}
                                className="min-w-0 flex-1 bg-transparent text-[12px] font-medium text-foreground outline-none"
                                autoFocus
                              />
                              <button
                                type="button"
                                className="rounded p-1 text-success hover:bg-success/10"
                                onClick={() => void submitRenameFolder(folder.id)}
                                disabled={folderBusy || !renamingFolderName.trim()}
                                aria-label="Speichern"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                className="rounded p-1 text-muted hover:bg-card-hover/80 dark:hover:bg-card-hover"
                                onClick={() => {
                                  setRenamingFolderId(null);
                                  setRenamingFolderName('');
                                }}
                                aria-label="Abbrechen"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                                onClick={() => toggleFolderCollapse(folder.id)}
                              >
                                <Folder className="h-4 w-4 shrink-0 text-accent" aria-hidden />
                                <span className="truncate text-[13px] font-medium text-foreground">
                                  {folder.name}
                                </span>
                              </button>
                              <div className="flex shrink-0 items-center gap-0.5 opacity-100 max-md:opacity-100 md:opacity-0 md:group-hover/fwrap:opacity-100">
                                <button
                                  type="button"
                                  className="rounded-md p-1 text-muted hover:bg-card-hover/90 hover:text-foreground dark:hover:bg-card-hover dark:hover:text-muted"
                                  title="Neuer Chat in diesem Projekt"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void createThreadInFolder(folder.id);
                                  }}
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  className="rounded-md p-1 text-muted hover:bg-card-hover/90 dark:hover:bg-card-hover"
                                  title="Projekt umbenennen"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setRenamingFolderId(folder.id);
                                    setRenamingFolderName(folder.name);
                                  }}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  className="rounded-md p-1 text-muted hover:bg-card-hover/90 hover:text-danger dark:hover:bg-card-hover"
                                  title="Projekt löschen"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void deleteFolder(folder.id);
                                  }}
                                  disabled={folderBusy}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                        {!collapsed && (
                          <div className="border-t border-border/60 px-1 pb-1 pt-0.5 dark:border-border/80">
                            {list.length === 0 ? (
                              <p className="py-2 pl-7 text-center text-[10px] text-muted">Leer</p>
                            ) : (
                              <div className="space-y-0.5">{list.map((t) => renderThreadRow(t))}</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div className="group/unfiled">
                    <div className="mb-1 flex items-center justify-between gap-1 px-1">
                      <div className="flex min-w-0 items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted">
                        <Inbox className="h-3.5 w-3.5 text-muted" aria-hidden />
                        Weitere Chats
                      </div>
                      <button
                        type="button"
                        className="rounded-md p-1 text-muted opacity-100 hover:bg-card-hover/80 dark:hover:bg-card-hover max-md:opacity-100 md:opacity-0 md:group-hover/unfiled:opacity-100"
                        title="Neuer Chat"
                        onClick={() => void createThreadInFolder(null)}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {(threadSections.byFolder.get(threadSections.NONE) ?? []).length === 0 ? (
                      <p className="px-1 py-2 text-center text-[10px] text-muted">Keine Chats</p>
                    ) : (
                      <div className="space-y-0.5">
                        {(threadSections.byFolder.get(threadSections.NONE) ?? []).map((t) => renderThreadRow(t))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex min-h-[500px] min-w-0 flex-col rounded-xl border border-border bg-card lg:h-[calc(100vh-300px)]">
          {agentChatHintName && (
            <div className="flex items-start gap-2 border-b border-border bg-card-hover/90 px-3 py-2.5 text-sm text-foreground dark:bg-primary-hover/40">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-foreground dark:text-muted" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="font-medium">Profil «{agentChatHintName}»</p>
                <p className="mt-0.5 text-xs text-muted">
                  {chatAgentId
                    ? 'Der Chat verwendet dieses Agenten-Profil (Modell und API-Endpunkt aus der Agenten-Konfiguration).'
                    : 'Formulieren Sie Ihre Aufgabe passend zum Profil.'}
                </p>
              </div>
              <div className="flex shrink-0 flex-col gap-1 sm:flex-row">
                {chatAgentId && (
                  <button
                    type="button"
                    onClick={() => {
                      setChatAgentId(null);
                      setAgentChatHintName(null);
                    }}
                    className="rounded border border-border bg-card px-2 py-0.5 text-xs font-medium text-foreground hover:bg-card-hover dark:text-primary-foreground dark:hover:bg-primary-hover/50"
                  >
                    Standard-KI
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setAgentChatHintName(null)}
                  className="rounded border border-border px-2 py-0.5 text-xs hover:bg-card/80 dark:hover:bg-card-hover"
                >
                  OK
                </button>
              </div>
            </div>
          )}
          <PlannerReviewPanel refreshKey={plannerRefreshVersion} />
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
              <span className="rounded-full bg-card-hover px-2 py-1 text-xs font-medium text-foreground dark:bg-primary-hover/30 dark:text-muted">
                Companion Chat
              </span>
              {chatAgentId ? (
                (() => {
                  const ag = agents.find((a) => a.id === chatAgentId);
                  return ag ? (
                    <span
                      className="inline-flex max-w-full items-center gap-1.5 truncate text-xs text-muted"
                      title={`Agentenprofil: ${companionProviderLabel(ag.provider)} · ${ag.model}`}
                    >
                      <Cpu size={12} className="shrink-0 text-muted" />
                      <span className="font-mono font-medium">{ag.model}</span>
                      <span className="hidden text-muted sm:inline">·</span>
                      <span className="hidden truncate text-muted sm:inline">{companionProviderLabel(ag.provider)}</span>
                      <span className="hidden text-[10px] font-normal text-muted sm:inline">(Profil)</span>
                    </span>
                  ) : (
                    <span className="text-xs text-muted">Profil wird geladen…</span>
                  );
                })()
              ) : statusLoading && !statusData ? (
                <span className="text-xs text-muted">Modell wird geladen…</span>
              ) : statusData ? (
                <span className="inline-flex max-w-full items-center gap-1.5 truncate text-xs text-muted" title={`Standard-KI: ${companionProviderLabel(statusData.provider)} · ${statusData.model}`}>
                  <Cpu size={12} className="shrink-0 text-muted" />
                  <span className="font-mono font-medium">{statusData.model}</span>
                  <span className="hidden text-muted sm:inline">·</span>
                  <span className="hidden truncate text-muted sm:inline">{companionProviderLabel(statusData.provider)}</span>
                  <span className="hidden text-[10px] font-normal text-muted sm:inline">(Standard)</span>
                </span>
              ) : null}
              <span className="text-xs text-muted">
                {activeThreadId ? 'Thread aktiv' : 'Kein Thread ausgewählt'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={startNewThread}
                className="rounded border border-border px-2 py-1 text-xs hover:bg-background dark:hover:bg-card-hover"
              >
                Neuer Thread
              </button>
              <button
                onClick={() => { threadLoadSeqRef.current += 1; setMessages([]); }}
                className="rounded border border-border px-2 py-1 text-xs hover:bg-background dark:hover:bg-card-hover"
              >
                Verlauf leeren
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Bot size={48} className="mb-4 text-muted" />
                <h3 className="text-lg font-medium text-muted">KI-Assistent bereit</h3>
                <p className="mt-1 max-w-md text-sm text-muted">
                  Stellen Sie Fragen zu Marketingstrategien, lassen Sie Texte generieren oder holen Sie sich Ideen für Kampagnen.
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                  {CHAT_EXAMPLES.slice(0, 4).map(s => (
                    <button key={s} onClick={() => setInput(s)}
                      className="rounded-full border border-border px-3 py-1.5 text-xs text-muted hover:border-border hover:bg-card-hover dark:hover:bg-primary-hover/20">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map(m => (
              <div key={m.id} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                <div className={cn(
                  'relative max-w-[75%] rounded-xl px-4 py-3 text-sm',
                  m.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card-hover text-foreground'
                )}>
                  {m.role === 'assistant' ? (
                    <MarkdownText
                      content={formatAssistantDisplayMarkdown(m.content)}
                      className="space-y-3 leading-relaxed"
                    />
                  ) : (
                    <div className="whitespace-pre-wrap">{m.content}</div>
                  )}
                  {m.role === 'assistant' && (m.actions?.length || 0) > 0 && (
                    <div className="mt-3 space-y-2 border-t border-border/60 pt-3 dark:border-border/50">
                      {m.actions?.map((action, index) => {
                        const actionKey = `${m.id}:${index}`;
                        const running = executingActionKey === actionKey;
                        return (
                          <div key={actionKey} className="rounded-lg border border-border bg-card/70 p-2 text-xs text-muted dark:bg-card-hover/60 dark:text-foreground">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium">{action.tool}</span>
                              <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-semibold', action.dryRun ? 'bg-warning-soft text-warning' : 'bg-success-soft text-success')}>
                                {action.dryRun ? 'dry-run' : 'ausgefuehrt'}
                              </span>
                            </div>
                            {action.input && (
                              <pre className="mt-2 max-h-24 overflow-auto rounded bg-card-hover p-2 text-[10px] text-muted dark:bg-card">{JSON.stringify(action.input, null, 2)}</pre>
                            )}
                            {action.dryRun && (
                              <button
                                onClick={() => executeSuggestedAction(m.id, index)}
                                disabled={running || !activeThreadId}
                                className="mt-2 inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] font-medium text-foreground hover:bg-card-hover disabled:opacity-50 dark:text-muted dark:hover:bg-primary-hover/30"
                              >
                                {running ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />}
                                Ausfuehren
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {m.role === 'assistant' && (
                    <button onClick={() => copyText(m.content, m.id)}
                      className="absolute -bottom-1 -right-1 rounded bg-card p-1 shadow-sm dark:bg-muted">
                      {copiedId === m.id ? <Check size={12} className="text-success" /> : <Copy size={12} className="text-muted" />}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-xl bg-card-hover px-4 py-3">
                  <Loader2 size={14} className="animate-spin text-foreground" />
                  <span className="text-xs text-muted">Generiere Antwort...</span>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          <div className="border-t border-border p-4">
            <div className="flex gap-2">
              <Textarea value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                placeholder="Nachricht eingeben..." rows={2}
                className="min-h-[52px] flex-1 resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm dark:bg-card dark:text-foreground focus:border-border focus:outline-none focus:ring-2 focus:ring-border/20" />
              <button onClick={sendChat} disabled={!input.trim() || sending} aria-label="Nachricht senden"
                className="rounded-lg bg-primary px-4 text-primary-foreground hover:bg-primary-hover disabled:opacity-50">
                <Send size={16} />
              </button>
            </div>
          </div>
          </div>
        </div>
      )}

      {activeTab === 'generate' && (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-border bg-card p-6 dark:bg-card-hover">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Eingabe</h3>
            <Textarea value={genInput} onChange={(e) => setGenInput(e.target.value)}
              placeholder="Beschreiben Sie, was generiert werden soll...&#10;&#10;Beispiel: Erstelle einen überzeugenden LinkedIn-Post über unsere neue Cloud-Lösung für KMU."
              rows={10}
              className="w-full resize-none rounded-lg border border-border bg-background p-3 text-sm dark:bg-card dark:text-foreground focus:border-border focus:outline-none" />
            <button onClick={handleGenerate} disabled={genLoading || !genInput.trim()}
              className="mt-3 flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50">
              {genLoading ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
              Generieren
            </button>
          </div>
          <div className="rounded-xl border border-border bg-card p-6 dark:bg-card-hover">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Ergebnis</h3>
              {genOutput && (
                <button onClick={() => copyText(genOutput, 'gen')} className="text-muted hover:text-foreground">
                  {copiedId === 'gen' ? <Check size={14} className="text-success" /> : <Copy size={14} />}
                </button>
              )}
            </div>
            <div className="min-h-[200px] rounded-lg border border-border bg-background p-3 text-sm whitespace-pre-wrap dark:bg-card dark:text-muted">
              {genOutput || <span className="text-muted">Ergebnis erscheint hier...</span>}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'improve' && (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-border bg-card p-6 dark:bg-card-hover">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Originaltext</h3>
            <Textarea value={improveInput} onChange={(e) => setImproveInput(e.target.value)}
              placeholder="Fügen Sie den Text ein, der verbessert werden soll..." rows={10}
              className="w-full resize-none rounded-lg border border-border bg-background p-3 text-sm dark:bg-card dark:text-foreground focus:border-border focus:outline-none" />
            <button onClick={handleImprove} disabled={improveLoading || !improveInput.trim()}
              className="mt-3 flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50">
              {improveLoading ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
              Verbessern
            </button>
          </div>
          <div className="rounded-xl border border-border bg-card p-6 dark:bg-card-hover">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Verbesserter Text</h3>
              {improveOutput && (
                <button onClick={() => copyText(improveOutput, 'improve')} className="text-muted hover:text-foreground">
                  {copiedId === 'improve' ? <Check size={14} className="text-success" /> : <Copy size={14} />}
                </button>
              )}
            </div>
            <div className="min-h-[200px] rounded-lg border border-border bg-background p-3 text-sm whitespace-pre-wrap dark:bg-card dark:text-muted">
              {improveOutput || <span className="text-muted">Verbesserter Text erscheint hier...</span>}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'translate' && (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-border bg-card p-6 dark:bg-card-hover">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Originaltext</h3>
            <Textarea value={translateInput} onChange={(e) => setTranslateInput(e.target.value)}
              placeholder="Text zum Übersetzen..." rows={8}
              className="w-full resize-none rounded-lg border border-border bg-background p-3 text-sm dark:bg-card dark:text-foreground focus:border-border focus:outline-none" />
            <div className="mt-3 flex items-center gap-3">
              <select value={translateLang} onChange={(e) => setTranslateLang(e.target.value)}
                className="rounded-lg border border-border bg-card px-3 py-2 text-sm dark:text-foreground">
                <option value="en">Englisch</option>
                <option value="fr">Französisch</option>
                <option value="it">Italienisch</option>
                <option value="es">Spanisch</option>
                <option value="de">Deutsch</option>
              </select>
              <button onClick={handleTranslate} disabled={translateLoading || !translateInput.trim()}
                className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50">
                {translateLoading ? <Loader2 size={14} className="animate-spin" /> : <Languages size={14} />}
                Übersetzen
              </button>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card p-6 dark:bg-card-hover">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Übersetzung</h3>
              {translateOutput && (
                <button onClick={() => copyText(translateOutput, 'translate')} className="text-muted hover:text-foreground">
                  {copiedId === 'translate' ? <Check size={14} className="text-success" /> : <Copy size={14} />}
                </button>
              )}
            </div>
            <div className="min-h-[200px] rounded-lg border border-border bg-background p-3 text-sm whitespace-pre-wrap dark:bg-card dark:text-muted">
              {translateOutput || <span className="text-muted">Übersetzung erscheint hier...</span>}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'research' && (
        <div className="flex min-h-[min(80vh,720px)] flex-col gap-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Deep Research</h3>
            <p className="text-xs text-muted">
              Markt- und Wettbewerbsanalyse mit Quellen — Liste links, Detail rechts. Azure Foundry kann mehrere Minuten
              dauern.
            </p>
            {researchProvider === 'azure_cognitive_services' && (
              <p className="mt-2 rounded-lg bg-accent-soft px-3 py-2 text-xs text-accent">
                Azure Cognitive Services (gpt-5.4-pro) — Credential in Admin hinterlegen.
              </p>
            )}
            {researchProvider === 'azure_foundry' && (
              <p className="mt-2 rounded-lg bg-warning-soft px-3 py-2 text-xs text-warning">
                Azure Foundry (Legacy) mit Bing — oft 3–15 Minuten Laufzeit.
              </p>
            )}
          </div>
          <ResearchWorkspace
            runs={researchRuns}
            scope="standalone"
            strategyTitle="Deep Research"
            strategyYear={new Date().getFullYear()}
            researchQuery={researchInput}
            setResearchQuery={setResearchInput}
            focusArea={researchFocusArea}
            setFocusArea={setResearchFocusArea}
            researchProvider={researchProvider}
            setResearchProvider={setResearchProvider}
            onStartResearch={() => void runResearch()}
            startingResearch={researchLoading}
            onRefresh={() => void fetchResearchRuns()}
            emptyHint={
              <div className="text-center text-sm text-muted">
                Noch keine gespeicherten Research-Läufe. Starte eine Anfrage oben — nach Abschluss erscheint sie in der
                Liste.
              </div>
            }
          />
        </div>
      )}

      {activeTab === 'agents' && (
        <div className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-foreground">KI-Agenten</h3>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="rounded-full p-1 text-muted outline-none ring-offset-2 hover:text-foreground focus-visible:ring-2 focus-visible:ring-border dark:hover:text-muted"
                      aria-label="Was sind KI-Agenten?"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-sm text-left leading-snug">
                    {AGENTS_TAB_EXPLANATION}
                  </TooltipContent>
                </Tooltip>
              </div>
              <p className="mt-1 text-sm text-muted">
                Pro Agent Modell und Provider wählbar («Modell & Profil bearbeiten»). Über «Zum Chat» nutzt der Companion dieses Profil; ohne Auswahl die globale Standard-KI aus den Einstellungen.
              </p>
              <p className="mt-2 text-xs text-muted">
                <span className="font-medium text-muted">Punkt rechts:</span>{' '}
                <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-warning" aria-hidden /> ungetestet</span>
                {' · '}
                <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-success" aria-hidden /> letzter Test OK</span>
                {' · '}
                <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-danger" aria-hidden /> letzter Test fehlgeschlagen</span>
                {' · '}
                <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-border dark:bg-muted" aria-hidden /> Profil inaktiv</span>
              </p>
            </div>
            <button onClick={() => setShowCreate(true)}
              className="flex shrink-0 items-center gap-2 self-start rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary-hover">
              <Plus size={16} /> Agent erstellen
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-10"><Loader2 size={24} className="animate-spin text-foreground" /></div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {agents.length === 0 && <p className="text-sm text-muted">{agentsError ? 'Agenten nicht verfügbar.' : 'Noch keine Agenten eingerichtet.'}</p>}
              {agents.map(agent => {
                const prov = PROVIDERS.find(p => p.value === agent.provider);
                const linkHealth = agentLinkHealth[agent.id] ?? 'untested';
                return (
                  <div
                    key={agent.id}
                    className="flex flex-col rounded-xl border border-border bg-card p-5 dark:bg-card-hover"
                  >
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-card-hover', prov?.color)}>
                          <Bot size={20} />
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-semibold text-foreground">{agent.name}</h4>
                          <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-xs text-muted">
                            {prov?.icon} <span>{prov?.label}</span> <span>/</span> <span className="font-mono">{agent.model}</span>
                          </div>
                        </div>
                      </div>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className={cn(
                              'h-2.5 w-2.5 shrink-0 cursor-help rounded-full ring-offset-2 ring-offset-white dark:ring-offset-background',
                              agentDotClass(agent.is_active, linkHealth),
                              agent.is_active && linkHealth === 'error' && 'ring-2 ring-danger/30'
                            )}
                            aria-label={agentDotAriaLabel(agent.is_active, linkHealth)}
                          />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-left leading-snug">
                          <p className="font-medium">
                            {agent.is_active ? 'Profil aktiv' : 'Profil inaktiv'}
                          </p>
                          <p className="mt-1 text-xs text-muted">
                            {!agent.is_active
                              ? 'Dieses Profil ist deaktiviert und wird nicht genutzt.'
                              : linkHealth === 'untested'
                                ? 'Farbe = letzter Verbindungstest, nicht nur „eingeschaltet“. Noch kein Test – bitte «Verbindung testen» ausführen.'
                                : linkHealth === 'ok'
                                  ? 'Letzter Verbindungstest war erfolgreich (API erreichbar).'
                                  : 'Letzter Verbindungstest ist fehlgeschlagen. Schlüssel, Endpunkt oder Netzwerk prüfen.'}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <div className="mb-3 rounded-lg border border-border bg-background/80 px-3 py-2.5 dark:border-border/80 dark:bg-card/40">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">
                          Ziel
                        </span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="rounded p-0.5 text-muted outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-border"
                              aria-label="Ziel und Einsatz: ausführliche Erklärung"
                            >
                              <HelpCircle size={14} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-sm text-left leading-snug">
                            <p className="text-xs font-medium text-foreground">Wofür dieser Agent gedacht ist</p>
                            <p className="mt-1.5 text-xs text-muted dark:text-foreground">{getAgentGoalText(agent)}</p>
                            {!agent.description?.trim() && (
                              <p className="mt-2 border-t border-border pt-2 text-[11px] text-muted">
                                Automatisch aus den Fähigkeiten abgeleitet. Eigenen Text unter «Modell & Profil bearbeiten» eintragen.
                              </p>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <p className="mt-1 line-clamp-4 text-xs leading-relaxed text-muted">
                        {getAgentGoalText(agent)}
                      </p>
                    </div>
                    <div className="mb-4 flex flex-wrap gap-1.5">
                      {agent.capabilities.map((c) => (
                        <Tooltip key={c}>
                          <TooltipTrigger asChild>
                            <span className="cursor-help rounded-full bg-card-hover px-2 py-0.5 text-xs font-medium text-foreground dark:bg-primary-hover/30 dark:text-muted">
                              {c.replace(/_/g, ' ')}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-left">
                            {CAPABILITY_HINTS[c] ?? `Fähigkeit: ${c.replace(/_/g, ' ')}`}
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                    <div className="mb-3">
                      {isPersistedAgentId(agent.id) ? (
                        <button
                          type="button"
                          onClick={() => openEditAgent(agent)}
                          className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-xs font-medium text-foreground hover:bg-background dark:hover:bg-card-hover"
                        >
                          <Pencil size={14} />
                          Modell & Profil bearbeiten
                        </button>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="block w-full">
                              <button
                                type="button"
                                disabled
                                className="inline-flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted"
                              >
                                <Pencil size={14} />
                                Modell & Profil bearbeiten
                              </button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            Nur für Agenten aus der Datenbank. Demo-Karten haben keine ID zum Speichern.
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    <div className="mt-auto flex flex-wrap gap-2 border-t border-border pt-4 dark:border-border/80">
                      <button
                        type="button"
                        onClick={() => {
                          setAgentChatHintName(agent.name);
                          setChatAgentId(agent.id);
                          setActiveTab('chat');
                        }}
                        className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border bg-card-hover px-3 py-2 text-xs font-medium text-foreground hover:bg-border dark:bg-primary-hover/50 dark:text-primary-foreground dark:hover:bg-primary-hover/40 sm:flex-initial"
                      >
                        <MessageSquare size={14} />
                        Zum Chat
                      </button>
                      <button
                        type="button"
                        onClick={() => testAgent(agent.id)}
                        className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-xs font-medium text-foreground hover:bg-card-hover dark:bg-card sm:flex-initial"
                      >
                        <Play size={14} />
                        Verbindung testen
                      </button>
                    </div>
                    {agentTestResult[agent.id] && (
                      <p className="mt-2 text-xs text-muted">{agentTestResult[agent.id]}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {showCreate && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50">
              <div className="w-full max-w-md rounded-xl bg-card p-6 shadow-2xl dark:bg-card-hover">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold dark:text-foreground">Neuer KI-Agent</h2>
                  <button onClick={() => setShowCreate(false)} className="text-muted hover:text-foreground"><X size={20} /></button>
                </div>
                {createAgentError && <p role="alert" className="mb-3 text-sm text-danger">{createAgentError}</p>}
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-muted">Name</label>
                    <input value={newAgent.name} onChange={(e) => setNewAgent(p => ({ ...p, name: e.target.value }))}
                      placeholder="z.B. MarketingBot"
                      className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm dark:text-foreground" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-muted">Provider</label>
                    <select value={newAgent.provider} onChange={(e) => setNewAgent(p => ({ ...p, provider: e.target.value }))}
                      className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm dark:text-foreground">
                      {PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-muted">Modell</label>
                    <select
                      value={createModelQuickPick.includes(newAgent.model) ? newAgent.model : ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v) setNewAgent((p) => ({ ...p, model: v }));
                      }}
                      className="mb-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm dark:text-foreground"
                      aria-label="Modell aus gespeicherten Einstellungen wählen"
                    >
                      <option value="">Schnellauswahl aus Einstellungen …</option>
                      {createModelQuickPick.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                    <input
                      value={newAgent.model}
                      onChange={(e) => setNewAgent((p) => ({ ...p, model: e.target.value }))}
                      placeholder="gpt-4o"
                      autoComplete="off"
                      className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm font-mono dark:text-foreground"
                    />
                    <p className="mt-1 text-[11px] leading-snug text-muted">{MODEL_FIELD_HINT}</p>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-muted">Fähigkeiten (kommagetrennt)</label>
                    <input value={newAgent.capabilities} onChange={(e) => setNewAgent(p => ({ ...p, capabilities: e.target.value }))}
                      placeholder="content_generation, translation, improvement"
                      className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm dark:text-foreground" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-muted">Ziel / Einsatz (optional)</label>
                    <Textarea
                      value={newAgent.description}
                      onChange={(e) => setNewAgent((p) => ({ ...p, description: e.target.value }))}
                      placeholder="Kurz: Wofür soll dieser Agent im Team genutzt werden?"
                      rows={3}
                      className="w-full resize-y rounded-lg border border-border bg-card px-3 py-2 text-sm dark:text-foreground"
                    />
                  </div>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                    <X className="h-4 w-4" />
                    Abbrechen
                  </Button>
                  <Button type="button" onClick={handleCreateAgent} disabled={!newAgent.name.trim()}>
                    <Plus className="h-4 w-4" />
                    Erstellen
                  </Button>
                </div>
              </div>
            </div>
          )}

          {editAgent && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 p-4">
              <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl bg-card p-6 shadow-2xl dark:bg-card-hover">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold dark:text-foreground">Agent bearbeiten</h2>
                  <button
                    type="button"
                    onClick={() => setEditAgent(null)}
                    className="text-muted hover:text-foreground"
                    aria-label="Schliessen"
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-muted">Name</label>
                    <input
                      value={editForm.name}
                      onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                      className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm dark:text-foreground"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-muted">Provider</label>
                    <select
                      value={editForm.provider}
                      onChange={(e) =>
                        setEditForm((p) => ({ ...p, provider: e.target.value as AIAgent['provider'] }))
                      }
                      className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm dark:text-foreground"
                    >
                      {PROVIDERS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-muted">Modell</label>
                    <select
                      value={editModelQuickPick.includes(editForm.model) ? editForm.model : ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v) setEditForm((p) => ({ ...p, model: v }));
                      }}
                      className="mb-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm dark:text-foreground"
                      aria-label="Modell aus gespeicherten Einstellungen wählen"
                    >
                      <option value="">Schnellauswahl aus Einstellungen …</option>
                      {editModelQuickPick.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                    <input
                      value={editForm.model}
                      onChange={(e) => setEditForm((p) => ({ ...p, model: e.target.value }))}
                      autoComplete="off"
                      className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm font-mono dark:text-foreground"
                    />
                    <p className="mt-1 text-[11px] leading-snug text-muted">{MODEL_FIELD_HINT}</p>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-muted">Fähigkeiten (kommagetrennt)</label>
                    <input
                      value={editForm.capabilities}
                      onChange={(e) => setEditForm((p) => ({ ...p, capabilities: e.target.value }))}
                      className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm dark:text-foreground"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-muted">Ziel / Einsatz</label>
                    <Textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                      placeholder="Wofür dieser Agent gedacht ist (wird auf der Karte angezeigt). Leer = automatisch aus Fähigkeiten."
                      rows={4}
                      className="w-full resize-y rounded-lg border border-border bg-card px-3 py-2 text-sm dark:text-foreground"
                    />
                  </div>
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-muted dark:text-foreground">
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm((p) => ({ ...p, is_active: e.target.checked }))}
                      className="rounded border-border"
                    />
                    Profil aktiv (nur aktive Profile können im Chat und bei Tests genutzt werden)
                  </label>
                  {editError && <p className="text-sm text-danger">{editError}</p>}
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setEditAgent(null)} disabled={editSaving}>
                    <X className="h-4 w-4" />
                    Abbrechen
                  </Button>
                  <Button type="button" onClick={() => void handleUpdateAgent()} disabled={editSaving}>
                    {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4" />}
                    Speichern
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'status' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Verbindung & Tests</h3>
            <button
              onClick={loadStatus}
              className="rounded-lg border border-border px-3 py-2 text-sm"
            >
              Neu laden
            </button>
          </div>

          <div className="rounded-xl border border-border bg-card p-4 dark:bg-card-hover">
            {statusLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted">
                <Loader2 size={14} className="animate-spin" /> Status wird geladen...
              </div>
            ) : statusData ? (
              <div className="space-y-1 text-sm">
                <p><strong>Provider:</strong> {statusData.provider}</p>
                <p><strong>Model:</strong> {statusData.model}</p>
                <p><strong>Verbunden:</strong> {getConnectionLabel(statusData.connected)}</p>
                <p className="text-muted">{statusData.provider_message}</p>
              </div>
            ) : (
              <p className="text-sm text-muted">Noch keine Statusdaten geladen.</p>
            )}
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            {agents.map((agent) => (
              <div key={agent.id} className="rounded-xl border border-border bg-card p-4 dark:bg-card-hover">
                <div className="mb-2 flex items-center justify-between">
                  <p className="font-medium">{agent.name}</p>
                  <button
                    onClick={() => testAgent(agent.id)}
                    className="inline-flex items-center gap-1 rounded-lg bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary-hover"
                  >
                    <Play size={12} /> Test
                  </button>
                </div>
                <p className="text-xs text-muted">{agent.provider} / {agent.model}</p>
                {agentTestResult[agent.id] && (
                  <p className="mt-2 text-xs text-muted">{agentTestResult[agent.id]}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
          <div className="w-full max-w-lg rounded-xl border border-border bg-card p-4 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-semibold">Gespräch teilen</h3>
              <button onClick={() => setShowShareModal(false)} className="rounded border px-2 py-1 text-xs">Schliessen</button>
            </div>

            <div className="grid gap-2 md:grid-cols-[1fr_120px_auto]">
              <input
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                placeholder="benutzer@firma.ch"
                className="rounded border border-border bg-card px-3 py-2 text-sm dark:bg-card-hover"
              />
              <select
                value={shareAccess}
                onChange={(e) => setShareAccess(e.target.value as 'view' | 'edit')}
                className="rounded border border-border bg-card px-2 py-2 text-sm dark:bg-card-hover"
              >
                <option value="view">View</option>
                <option value="edit">Edit</option>
              </select>
              <button
                onClick={shareCurrentThread}
                disabled={sharingBusy || !shareEmail.trim()}
                className="rounded bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50"
              >
                Teilen
              </button>
            </div>

            <div className="mt-4 space-y-2">
              <p className="text-xs font-medium text-muted">Freigegeben an</p>
              {shares.length === 0 && <p className="text-sm text-muted">Noch keine Freigaben vorhanden.</p>}
              {shares.map((share) => (
                <div key={share.id} className="flex items-center justify-between rounded border border-border px-3 py-2 text-sm">
                  <div>
                    <p>{share.shared_user?.email || share.shared_with_user_id}</p>
                    <p className="text-xs text-muted">Recht: {share.access_level}</p>
                  </div>
                  <button
                    onClick={() => revokeShare(share.id)}
                    disabled={sharingBusy}
                    className="rounded border px-2 py-1 text-xs"
                  >
                    Entziehen
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
    </TooltipProvider>
  );
}
